import React, {useEffect, useRef, useState, useCallback, useMemo} from "react";
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    Tooltip,
    Filler,
    LineController,
    CategoryScale,
} from "chart.js";
import {Line} from "react-chartjs-2";

ChartJS.register(
    LineElement,
    PointElement,
    LinearScale,
    Tooltip,
    Filler,
    LineController,
    CategoryScale
);


const verticalHoverLinePlugin = {
    id: "verticalHoverLine",

    afterDraw(chart) {
        const activeElements = chart.getActiveElements();
        if (!activeElements.length) return;

        const {ctx, chartArea} = chart;
        if (!chartArea) return;

        const {x, y} = activeElements[0].element;
        const color = chart.data.datasets[0]?.borderColor || "#10b981";

        const OFFSET_ABOVE = 24; // how far the line extends above the chart line (px)
        const topY = Math.max(chartArea.top, y - OFFSET_ABOVE);

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, chartArea.bottom);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = color;
        ctx.setLineDash([]);
        ctx.stroke();
        ctx.restore();
    },
};

const formatTooltipLabel = (raw, timeframe) => {
    if (raw === "" || raw == null) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);

    // ALL: only the year
    if (timeframe === "ALL") return String(d.getFullYear());

    // 1Y: month + year
    if (timeframe === "1Y") {
        return d.toLocaleDateString("en-US", {month: "long", year: "numeric"});
    }

    // 1D: date + hour
    if (timeframe === "1D") {
        return d.toLocaleString("en-US", {
            month: "long",
            day: "2-digit"
        });
    }

    // 7D / 1M: "October 03"
    return d.toLocaleDateString("en-US", {month: "long", day: "2-digit"});
};
const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

const DEFAULT_STEP = {"1D": 4 * HOUR, "7D": DAY, "1M": 5 * DAY, "1Y": 30 * DAY};

const buildSeries = (labels, data, timeframe) => {
    const baseLabels = labels.length === 1 ? [labels[0], labels[0]] : labels;
    const baseData = data.length === 1 ? [data[0], data[0]] : data;

    const first = baseLabels.length ? new Date(baseLabels[0]).getTime() : NaN;
    if (isNaN(first)) return {labels: baseLabels, data: baseData, offset: 0};

    const gaps = [];
    for (let i = 1; i < baseLabels.length; i++) {
        const g = new Date(baseLabels[i]).getTime() - new Date(baseLabels[i - 1]).getTime();
        if (g > 0) gaps.push(g);
    }
    gaps.sort((a, b) => a - b);
    let step = gaps.length ? gaps[Math.floor(gaps.length / 2)] : DEFAULT_STEP[timeframe] || DAY;
    if (timeframe === "7D") step = DAY;

    let need = 0;
    if (timeframe === "7D") {
        const days = new Set(baseLabels.map((l) => new Date(l).toDateString())).size;
        need = Math.max(0, 7 - days);
    } else if (baseLabels.length < 5) {
        need = 6 - baseLabels.length;
    }

    // const prefix = Array.from({length: need}, (_, i) =>
    //     new Date(first - step * (need - i)).toISOString()
    // );

    // This is for LEFT padding in the chart empty points -MM

    const last = new Date(baseLabels[baseLabels.length - 1]).getTime();
    const suffix = Array.from({length: need}, (_, i) =>
        new Date(last + step * (i + 1)).toISOString()
    );

    // This is for RIGHT padding in the chart empty points -MM


    // return {
    //     labels: [...prefix, ...baseLabels],
    //     data: [...Array(need).fill(null), ...baseData],
    //     offset: need,
    // };
    // This is for LEFT padding in the chart empty points -MM

    return {
        labels: [...baseLabels, ...suffix],
        data: [...baseData, ...Array(need).fill(null)],
        offset: 0,
    };
    // This is for RIGHT padding in the chart empty points -MM

};

const PriceChart = React.memo(
    ({
         openChart,
         labels = [],
         data = [],
         symbol,
         currentTimeframe,
         setPriceColor,
         setDisplayedPrice,
         displayedPrice,
     }) => {
        const [toolTip, setTooltip] = useState(null);
        const chartRef = useRef(null);
        const prevPrice = useRef(displayedPrice);

        const hasData = Array.isArray(data) && data.length > 0;

        const chartColor = useMemo(() => {
            if (!hasData) return "#10b981";
            if (data.length === 1) return "#10b981";
            return data[data.length - 1] >= data[0] ? "#10b981" : "#ef4444";
        }, [data, hasData]);

        const currentPrice = hasData ? data[data.length - 1] : 0;

        const isSinglePoint = data.length === 1;

        const {labels: chartLabels, data: chartValues, offset} = useMemo(
            () => buildSeries(labels, data, currentTimeframe),
            [labels, data, currentTimeframe]
        );

        const chartData = useMemo(
            () => ({
                labels: chartLabels,
                datasets: [
                    {
                        label: symbol,
                        data: chartValues,
                        borderColor: chartColor,
                        backgroundColor: (ctx) => {
                            const gradient = ctx.chart.ctx.createLinearGradient(0, 0, 0, 300);
                            gradient.addColorStop(0, chartColor + "30");
                            gradient.addColorStop(1, chartColor + "05");
                            return gradient;
                        },
                        borderWidth: 3,
                        fill: true,
                        tension: 0.35,
                        cubicInterpolationMode: "monotone",
                        spanGaps: false,
                        pointRadius: 0,
                        pointHoverRadius: (ctx) => {
                            if (!isSinglePoint) return 6;
                            return ctx.dataIndex === offset + 1 ? 6 : 0;
                        },
                        pointHoverBackgroundColor: chartColor,
                        pointHoverBorderColor: chartColor,
                        pointHoverBorderWidth: 0,
                        // pointHoverRadius: (ctx) => {
                        //     if (!isSinglePoint) return 8;
                        //     return ctx.dataIndex === offset + 1 ? 8 : 0;
                        // },
                        // pointHoverBackgroundColor: chartColor,
                        // pointHoverBorderColor: "#fff",
                        // pointHoverBorderWidth: 3,
                    },
                ],
            }),
            [chartLabels, chartValues, offset, symbol, chartColor, isSinglePoint]
        );

        // const formatDateLabel = (raw) => {
        //     if (raw === "" || raw == null) return "";
        //     const d = new Date(raw);
        //     if (isNaN(d.getTime())) return String(raw);
        //     return d.toLocaleString(undefined, {
        //         month: "short",
        //         day: "numeric",
        //         // hour: "numeric",
        //         // minute: "2-digit",
        //     });
        // };

        const formatAxisLabel = (raw, timeframe) => {
            if (raw === "" || raw == null) return "";

            const d = new Date(raw);
            if (isNaN(d.getTime())) return String(raw);

            // One day: show only the hour in 24-hour format (e.g. 03)
            if (timeframe === "1D") {
                return String(d.getHours()).padStart(2, "0");
            }

            // One week: show the weekday
            if (timeframe === "7D") {
                return d.toLocaleDateString("en-US", {
                    weekday: "short",
                });
            }

            // One month: show only the day number (e.g. 09)
            if (timeframe === "1M") {
                return String(d.getDate()).padStart(2, "0");
            }

            // One year: show only the month (e.g. Jan)
            if (timeframe === "1Y") {
                return d.toLocaleDateString("en-US", {
                    month: "short",
                });
            }

            // ALL: only the year
            return String(d.getFullYear());
        };


        const externalTooltipHandler = useCallback(
            (context) => {
                const tooltipModel = context.tooltip;

                if (tooltipModel.opacity === 0) {
                    setTooltip(null);
                    return;
                }

                if (tooltipModel.dataPoints?.length > 0) {
                    const dataPoint = tooltipModel.dataPoints[0];
                    const realIndex = isSinglePoint ? 0 : dataPoint.dataIndex - offset;
                    if (realIndex < 0) {
                        setTooltip(null);
                        return;
                    }

                    const rawLabel = labels[realIndex] ?? "";
                    const newTooltip = {
                        label: formatTooltipLabel(rawLabel, currentTimeframe),
                        x: dataPoint.element.x,
                        y: dataPoint.element.y,
                        width: context.chart.width,
                        index: realIndex,
                    };

                    setTooltip((prev) =>
                        prev &&
                        prev.x === newTooltip.x &&
                        prev.y === newTooltip.y &&
                        prev.label === newTooltip.label
                            ? prev
                            : newTooltip
                    );
                }
            },
            [labels, offset, isSinglePoint, currentTimeframe]
        );

        const yMin = useMemo(() => {
            if (!hasData) return 0;
            const min = Math.min(...data);
            return data.length === 1 ? min - min * 0.02 : min * 0.92;
        }, [data, hasData]);

        const yMax = useMemo(() => {
            if (!hasData) return 100;
            const max = Math.max(...data);
            return data.length === 1 ? max + max * 0.02 : max * 1.08;
        }, [data, hasData]);

        const chartOptions = useMemo(
            () => ({
                layout: {padding: {top: 36}},
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    tooltip: {
                        enabled: false,
                        external: externalTooltipHandler,
                    },
                    legend: {
                        display: false,
                    },
                },
                scales: {
                    x: {
                        display: hasData,
                        grid: {display: false},
                        border: {display: false},
                        ticks: {
                            color: "#9ca3af",
                            font: {size: 11},
                            autoSkip: false,
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                                const total = chartLabels.length;
                                const text = formatAxisLabel(chartLabels[index], currentTimeframe);

                                if (index > 0 && text === formatAxisLabel(chartLabels[index - 1], currentTimeframe)) {
                                    return null;
                                }

                                if (currentTimeframe === "7D" || total <= 10) return text;

                                const maxTicks = 7;
                                const step = Math.ceil((total - 1) / (maxTicks - 1));
                                if (index === 0 || index === total - 1 || index % step === 0) return text;
                                return null;
                            },
                        },
                    },
                    y: {
                        display: false,
                        min: yMin,
                        max: yMax,
                        ticks: {
                            stepSize: hasData ? (yMax - yMin) / 5 : 20,
                        },
                    },
                },
                interaction: {
                    intersect: false,
                    mode: "index",
                },
                hover: {
                    animationDuration: 1,
                },
                elements: {
                    point: {
                        hoverRadius: 8,
                    },
                },
                onHover: (event, activeElements) => {
                    const chart = chartRef.current;
                    if (!chart || !hasData) return;

                    if (activeElements.length > 0) {
                        const dataIndex = isSinglePoint ? 0 : activeElements[0].index - offset;
                        const price = dataIndex >= 0 ? data[dataIndex] : undefined;

                        if (price !== undefined && !isNaN(price)) {
                            setDisplayedPrice(price);
                            event.native.target.style.cursor = "pointer";
                        } else {
                            setDisplayedPrice(currentPrice);
                            event.native.target.style.cursor = "default";
                        }
                    } else {
                        setDisplayedPrice(currentPrice);
                        event.native.target.style.cursor = "default";
                    }
                },
            }),
            [externalTooltipHandler, hasData, data, currentPrice, setDisplayedPrice, yMin, yMax, currentTimeframe, chartLabels, offset, isSinglePoint]
        );

        useEffect(() => {
            if (displayedPrice == null) return;

            if (prevPrice.current == null) {
                prevPrice.current = displayedPrice;
                return;
            }

            if (displayedPrice > prevPrice.current) {
                setPriceColor("text-[#10b981]");
            } else if (displayedPrice < prevPrice.current) {
                setPriceColor("text-[#ef4444]");
            } else {
                setPriceColor("text-primary");
            }

            prevPrice.current = displayedPrice;

            const timeoutId = setTimeout(() => {
                setPriceColor("text-primary");
            }, 1000);

            return () => clearTimeout(timeoutId);
        }, [displayedPrice, setPriceColor]);

        useEffect(() => {
            setPriceColor("text-primary");
        }, [currentTimeframe, setPriceColor]);

        if (!openChart) return null;

        return (
            <div className="relative w-full h-full bg-white rounded">
                {toolTip && (
                    <div
                        className="absolute text-base font-semibold text-gray-900 whitespace-nowrap"
                        style={{
                            left: `${Math.min(Math.max(toolTip.x, 60), toolTip.width - 60)}px`,
                            top: `${Math.max(0, toolTip.y - 24 - 32)}px`,
                            transform: "translateX(-50%)",
                            pointerEvents: "none",
                        }}
                    >
                        {toolTip.label}
                    </div>
                )}
                <Line
                    ref={chartRef}
                    data={chartData}
                    options={chartOptions}
                    plugins={[verticalHoverLinePlugin]}
                />
            </div>
        );
    }
);

export default PriceChart;
